import { useEffect, useState, useCallback } from "react";
import { LOGO, FINAL_CTA } from "@/data/content";

const KEY = "ign_intro_seen";

/**
 * Cinematic intro overlay — exactly 3 seconds.
 *
 * Timeline:
 *   0.0–0.6 s  Cat/logo fades in progressively
 *   0.6–1.3 s  Blue illumination of the cat (eyes + contours)
 *   1.3–2.2 s  Luminous cleaning wave (squeegee) sweeps across
 *   2.2–3.0 s  Slogan appears, then overlay fades out to homepage
 *
 * Shown once per session. Skip button always available.
 */
export default function IntroOverlay() {
    const [show, setShow] = useState(() => {
        try { return !sessionStorage.getItem(KEY); } catch { return true; }
    });
    const [out, setOut] = useState(false);

    const dismiss = useCallback(() => {
        setOut(true);
        try { sessionStorage.setItem(KEY, "1"); } catch { /* no-op */ }
        window.setTimeout(() => {
            setShow(false);
            document.body.classList.remove("no-scroll");
        }, 650);
    }, []);

    useEffect(() => {
        if (!show) return;
        document.body.classList.add("no-scroll");
        const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        /* Dismiss triggers at 2350ms → 650ms fade → total ≈ 3s */
        const t = window.setTimeout(dismiss, reduce ? 800 : 2350);
        return () => window.clearTimeout(t);
    }, [show, dismiss]);

    if (!show) return null;

    return (
        <div className={"intro-root" + (out ? " out" : "")} data-testid="intro-overlay" role="dialog" aria-label="Introduction IGN Services Alsace">
            {/* Dust particles swept away by the squeegee */}
            <div className="intro-dust" aria-hidden="true" />
            {/* Luminous squeegee wave */}
            <div className="intro-squeegee" aria-hidden="true" />

            <div className="intro-content">
                <div className="intro-logo-wrap">
                    <img className="intro-logo" src={LOGO} alt="IGN Services Alsace" />
                    <span className="intro-cat-glow" aria-hidden="true" />
                </div>
                <p className="intro-slogan">{FINAL_CTA.slogan}</p>
            </div>

            <button className="intro-skip" onClick={dismiss} data-testid="intro-skip-btn">
                Passer
            </button>
        </div>
    );
}
